import React from 'react';
import { ButtonBase, Radio, Typography, Box } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';

/** Shared border/fill for selectable cards (radio cards, type tiles). */
export const choiceSurfaceSx = (selected: boolean) => (theme: Theme) => ({
  borderRadius: 3,
  border: `${selected ? 2 : 1}px solid`,
  borderColor: selected ? theme.palette.primary.dark : theme.palette.divider,
  bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
  transition: 'border-color 120ms, background-color 120ms',
  '&:hover': { borderColor: theme.palette.primary.dark },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
});

interface ChoiceCardProps {
  title: string;
  subtitle?: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

/** Radio-style card; place inside an element with role="radiogroup". */
const ChoiceCard: React.FC<ChoiceCardProps> = ({ title, subtitle, selected, onSelect, disabled }) => (
  <ButtonBase
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onSelect}
    sx={[
      choiceSurfaceSx(selected),
      // A 2px selected border would otherwise shift the content by 1px.
      { width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.5, py: selected ? '13px' : '14px', px: selected ? '15px' : 2 },
    ]}
  >
    <Radio checked={selected} tabIndex={-1} size="small" disableRipple inputProps={{ 'aria-hidden': true }} sx={{ p: 0, color: 'text.secondary', '&.Mui-checked': { color: 'primary.dark' } }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{subtitle}</Typography>}
    </Box>
  </ButtonBase>
);

export default ChoiceCard;
